import { useState } from "react";
import { Sparkles, Loader2, Check, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ResponsiveDialog as Dialog,
  ResponsiveDialogContent as DialogContent,
  ResponsiveDialogDescription as DialogDescription,
  ResponsiveDialogFooter as DialogFooter,
  ResponsiveDialogHeader as DialogHeader,
  ResponsiveDialogTitle as DialogTitle,
  ResponsiveDialogBody as DialogBody,
} from "@/components/ui/responsive-dialog";
import { Badge } from "@/components/ui/badge";
import { useAiStatus } from "@/hooks/useAiStatus";
import api from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * "Rydd opp med AI" – turns dictated/rough notes into clean report text.
 * Shows before/after; the user chooses "Bruk forslag" or "Ignorer". Never auto-applies.
 *
 * context: "service" | "jobb" | "defects"
 * onApply(result) receives {title, description, work_performed[], defects, follow_up}
 */
export const AiTextAssist = ({ text, context = "jobb", machineLabel, onApply, testid = "ai-cleanup-text-button", size = "sm", className }) => {
  const ai = useAiStatus();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [open, setOpen] = useState(false);

  if (!ai.enabled) return null;

  const run = async () => {
    if (!text || !text.trim()) {
      toast.info("Skriv eller snakk inn nokre notat først.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/ai/clean-report-text", { text, context, machine_label: machineLabel || null });
      setResult(r.data);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI kunne ikkje rydde teksten. Du kan skrive vidare manuelt.");
    } finally {
      setBusy(false);
    }
  };

  const apply = () => {
    onApply?.(result);
    setOpen(false);
    toast.success("AI-forslaget er lagt inn. Les gjennom og juster ved behov.");
  };

  return (
    <>
      <Button
        type="button"
        size={size}
        variant="outline"
        data-testid={testid}
        onClick={run}
        disabled={busy}
        className={cn("border-primary/40 text-foreground hover:bg-primary/10", className)}
      >
        {busy ? <Loader2 className="animate-spin" /> : <Sparkles className="text-primary" />}
        {busy ? "Ryddar …" : "Rydd opp med AI"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[92vh] flex-col gap-0 p-0 sm:max-w-3xl" drawerClassName="max-h-[94dvh]" data-testid="ai-cleanup-dialog">
          <DialogHeader className="md:border-b md:px-6 md:pb-4 md:pt-6">
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" /> AI-forslag – kontroller
            </DialogTitle>
            <DialogDescription>
              AI har rydda notata dine til rapporttekst. Ingenting blir lagra før du vel «Bruk forslag». Du kan redigere alt etterpå.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="md:min-h-0 md:flex-1 md:overflow-y-auto md:px-6 md:py-4">
          {result && (
            <div className="grid gap-4 md:grid-cols-[1fr_auto_1fr] md:items-start">
              <div className="rounded-lg border bg-muted/40 p-4">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Før (dine notat)</div>
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">{result.original}</p>
              </div>
              <div className="hidden items-center justify-center pt-10 md:flex"><ArrowRight className="h-5 w-5 text-muted-foreground" /></div>
              <div className="ai-card p-4" data-testid="ai-cleanup-result">
                <div className="mb-2 flex items-center gap-2">
                  <Badge variant="outline" className="border-primary/40 text-[10px] uppercase tracking-wider">AI-forslag</Badge>
                </div>
                {result.title && (
                  <div className="mb-2">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Tittel</div>
                    <div className="font-semibold">{result.title}</div>
                  </div>
                )}
                {result.work_performed?.length > 0 && (
                  <div className="mb-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Utført arbeid</div>
                    <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm">
                      {result.work_performed.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                )}
                {result.description && (
                  <div className="mb-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Skildring</div>
                    <p className="mt-1 whitespace-pre-wrap text-sm">{result.description}</p>
                  </div>
                )}
                {result.defects && (
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Avvik / merknader</div>
                    <p className="mt-1 whitespace-pre-wrap text-sm">{result.defects}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          </DialogBody>
          <DialogFooter className="gap-2 md:border-t md:px-6 md:py-3 sm:gap-0">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="ai-cleanup-ignore">
              <X /> Ignorer
            </Button>
            <Button type="button" onClick={apply} data-testid="ai-cleanup-apply">
              <Check /> Bruk forslag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
